import ApexCharts from 'apexcharts';
import {
  GetRemediationActionsResponse,
  RemediationActions,
  REMEDIATION_ACTIONS_NUM_DECIMALS,
  Status,
} from '@nirvana/shared/dtos/types/widgets/common';
import { DisplayMode, TemplateState } from '../../types/common/types';
import { roundToDecimals } from '../../utils/formatter.utils';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { BaseThreatWidgetView } from '../../widget/BaseThreatWidgetView';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import {
  CommonThreatsNavigationHelper,
  NavigationItem,
  RemediationActionsNavigationHelper,
} from '@nirvana/shared/navigation';

export class RemediationsView extends BaseThreatWidgetView {
  // If data labels inside the chart don't have at least this number of pixels on the left and on the right,
  // don't show the label.
  private static MIN_PADDING_DATA_LABELS = 5;

  public latestData: any;
  public shadow: any;
  protected translations: any;
  private readonly $chartContainer: HTMLElement;
  private $chart: any;
  private $style: any;
  private chartColors: Array<string>;
  private errorColor: string;
  private basicChartOptions: any;

  private static roundTo2DecimalsAndAppUpTo100(data: RemediationActions): RemediationActions {
    let maxKey;
    let max = 0;
    let sum = 0;

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        // The percents from the backend come in [0, 1] interval. I.e., 0.23 means 23%. If we
        // want percents with 2 decimals after ., i.e., 23.78%, that means 4 decimals in [0, 1], i.e.
        // 0.2378. That's why we need to round to 4 decimals here.
        data[key] = roundToDecimals(data[key], REMEDIATION_ACTIONS_NUM_DECIMALS);

        sum += data[key];

        if (max < data[key]) {
          max = data[key];
          maxKey = key;
        }
      }
    }

    // Take the maximum number and add / substract value so that the sum is 100%.
    data[maxKey] += 1 - sum;

    return data;
  }

  getDomElements() {
    super.getDomElements();
    this.dom.$chartContainer = this.shadow.querySelector('#remediationActionsChart');
    this.dom.$widgetTitleString = this.shadow.querySelector('#remediationActionsTitle');
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$textWidthMeasurer = this.shadow.querySelector('#textWidthMeasurer');
    this.$style = getComputedStyle(document.body);
    this.getTranslations();
    this.setTranslatedStrings();
    this.getChartColors();
    this.setErrorTooltip();
    this.setBasicChartOptions();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getRemediationsActionsWidgetStrings();
  }

  setTranslatedStrings() {
    const { title } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;
  }

  private getChartColors() {
    const color1 = this.$style.getPropertyValue('--red-chart-600');
    const color2 = this.$style.getPropertyValue('--orange-alert-chart-100');
    const color3 = this.$style.getPropertyValue('--purple-chart-050');
    const color4 = this.$style.getPropertyValue('--blue-chart-500');
    const color5 = this.$style.getPropertyValue('--green-teal-chart-300');

    this.chartColors = [color1, color2, color3, color4, color5];

    /* we need to trim the whitespace before the color value because it will
     break the color in the tooltip template
     */
    for (let i = 0; i < this.chartColors.length; i++) {
      this.chartColors[i] = this.chartColors[i].trimLeft();
    }

    this.errorColor = this.$style.getPropertyValue('--chart-101-pressed-dividers').trimLeft();
  }

  private setBasicChartOptions() {
    this.basicChartOptions = {
      chart: {
        toolbar: {
          show: false,
        },
        width: '100%',
        height: '200px',
        type: 'bar',
        stacked: true,
        stackType: '100%',
        parentHeightOffset: 8,
        events: {
          updated: (chartContext, config) => {
            this.updateDrillDownHandlers();
          },
          dataPointMouseEnter: (mouseEvent: MouseEvent, options: any, config: any) => {
            this.alignTooltip(mouseEvent, options, config);
          },
          mouseMove: (mouseEvent: MouseEvent, options: any, config: any) => {
            this.alignTooltip(mouseEvent, options, config);
          },
        },
        animations: {
          enabled: true,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          columnWidth: '100%',
          barHeight: '65%',
        },
      },
      stroke: {
        show: false,
      },
      xaxis: {
        categories: [''],
        tickAmount: 10,
        axisBorder: {
          show: true,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          formatter(value) {
            return value !== 0 ? `${value}%` : `${value}`;
          },
        },
      },
      yaxis: {
        crosshairs: {
          show: false,
        },
      },
      grid: {
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'left',
        offsetX: 0,
        offsetY: 8,
        fontFamily: this.$style.getPropertyValue('--font-family-secondary'),
        markers: {
          width: 10,
          height: 10,
          strokeWidth: 0,
          radius: 0,
        },
        itemMargin: {
          horizontal: 0,
          vertical: 0,
        },
        onItemClick: {
          toggleDataSeries: false,
        },
        onItemHover: {
          highlightDataSeries: false,
        },
      },
      states: {
        active: {
          filter: {
            type: 'none',
          },
        },
        hover: {
          filter: {
            type: 'lighten',
            value: 0.05,
          },
        },
      },
    };
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  buildSuccessView(response: BackendResponseWithMeta<GetRemediationActionsResponse>) {
    this.resetTemplateState(this.$wrapper);
    this.updateChartAnimationSetting(response[DASHBOARD_METADATA_KEY].displayMode);

    const result = response.widgetResult;

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.buildEmptyTemplate();
      return;
    }

    if (result.data && result.status === Status.ACTIVE) {
      this.latestData = RemediationsView.roundTo2DecimalsAndAppUpTo100(result.data);
      const { labels } = this.translations;
      const chartSeries = [
        {
          name: labels.blocked,
          data: [this.latestData.blocked],
        },
        {
          name: labels.deleted,
          data: [this.latestData.deleted],
        },
        {
          name: labels.maliciousProcesses,
          data: [this.latestData.processesKilled],
        },
        {
          name: labels.quarantine,
          data: [this.latestData.quarantined],
        },
        {
          name: labels.disinfected,
          data: [this.latestData.disinfected],
        },
      ];

      this.dashboardMetaData = {
        targets: response[DASHBOARD_METADATA_KEY].targets,
        timeRange: response[DASHBOARD_METADATA_KEY].reportingPeriod.timeRange,
        canDrillDown: response[DASHBOARD_METADATA_KEY].canDrillDown,
      };
      this.buildChart(chartSeries, this.getChartOptions(chartSeries));
      this.addDrillDownHandlers(
        this.dashboardMetaData.targets,
        this.dashboardMetaData.timeRange,
        this.dashboardMetaData.canDrillDown
      );
      return;
    }

    this.buildErrorView();
  }

  private alignTooltip(mouseEvent: MouseEvent, options: any, config: any): void {
    const tooltipWidth = options.el.querySelector('.apexcharts-tooltip')?.clientWidth;
    const graphWidth = options.el.clientWidth;
    const positionTooltip = this.getTooltipPosition(mouseEvent.offsetX, graphWidth, tooltipWidth);

    options.el.style.setProperty('--tooltip-left-position', positionTooltip + 'px');
  }

  private getTooltipPosition(mousePosition: number, elementWidth: number, tooltipWidth: number): number {
    if (mousePosition > elementWidth - tooltipWidth) {
      return mousePosition - tooltipWidth;
    }

    return mousePosition;
  }
  private buildEmptyTemplate() {
    const { labels } = this.translations;
    const chartSeries: any[] = [];
    for (const value in labels) {
      if (labels.hasOwnProperty(value)) {
        chartSeries.push({ name: labels[value], data: [0.2] });
      }
    }

    this.buildChart(chartSeries, this.getErrorChartOptions(chartSeries));
  }

  private getErrorChartOptions(chartSeries) {
    return {
      ...this.basicChartOptions,
      colors: [this.errorColor, this.errorColor, this.errorColor, this.errorColor, this.errorColor],
      series: chartSeries,
      dataLabels: {
        enabled: false,
      },
      states: {
        normal: {
          filter: {
            type: 'none',
          },
        },
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
      legend: {
        ...this.basicChartOptions.legend,
        // Needed to set formatter of the legend, to avoid coming up with this from the success case,
        // which also shows the percents - see https://jira.bitdefender.biz/browse/CERES-16436.
        formatter: function (val) {
          return val;
        },
      },
    };
  }

  private getChartOptions(chartSeries) {
    const textWidthMeasurer = this.dom.$textWidthMeasurer;

    return {
      ...this.basicChartOptions,
      colors: this.chartColors,
      series: chartSeries,
      dataLabels: {
        enabled: true,
        style: {
          fontSize: this.$style.getPropertyValue('--body-size'),
          fontFamily: this.$style.getPropertyValue('--font-family-secondary-mono'),
          fontWeight: 'normal',
        },
        formatter: function (val, opt) {
          // At this point, values should be rounded to 2 decimals. However, ApexCharts multiplies the
          // values with 100, to get percentages in [0, 100] range. This multiplication can result in rounding
          // errors. I.e., try 0.3305 * 100 in Javascript. You'll get 33.050000000000004. That's why we need
          // to round again.
          const textToDisplay = `${roundToDecimals(val, 2)}%`;

          // Best effort to hide the value, i.e., if searching the DOM fails we still show the widget as is.
          // TODO(fburuiana, ivlasceanu): Maybe find a better solution.
          const seriesElems = opt.w.globals.dom.baseEl.getElementsByClassName('apexcharts-series');
          if (seriesElems && seriesElems[opt.seriesIndex]) {
            const svgElem = (seriesElems[opt.seriesIndex].childNodes || [])[0];
            if (svgElem) {
              const barWidth = svgElem.getAttribute('barWidth');

              textWidthMeasurer.innerHTML = textToDisplay;
              if (textWidthMeasurer.clientWidth + 2 * RemediationsView.MIN_PADDING_DATA_LABELS > barWidth) {
                return '';
              }
            }
          }

          return textToDisplay;
        },
      },

      tooltip: {
        enabled: true,
        followCursor: true,
        intersect: true,
        custom({ series, seriesIndex, w }) {
          return `
              <div class="tooltip">
                <span class='tooltip--bullet' style="background-color: ${w.globals.colors[seriesIndex]}"></span>
                <span class='tooltip--label'>${w.globals.seriesNames[seriesIndex]}: </span>
                <span class='tooltip--value'>${roundToDecimals(series[seriesIndex] * 100, 2)}%</span>
              </div>
          `;
        },
      },

      legend: {
        ...this.basicChartOptions.legend,
        formatter: function (val, opt) {
          return `${val}: <b>${roundToDecimals(opt.w.globals.series[opt.seriesIndex] * 100, 2)}%</b>`;
        },
      },
    };
  }

  private buildChart(chartSeries, options) {
    if (!this.$chart) {
      this.$chart = new ApexCharts(this.dom.$chartContainer, options);
      this.$chart.render();
    } else {
      this.$chart.updateSeries(chartSeries, true);
      this.$chart.updateOptions(options);
    }
  }

  protected getNavigationItemForTitle(targets: Array<Target>, timeRange: number): NavigationItem {
    const isPartner = Application.Main.company.isPartner();
    const filters = RemediationActionsNavigationHelper.getFiltersForNavigationItem({
      itemKey: 'title',
      timeRange: timeRange,
      targets: targets,
      isPartner: isPartner,
    });
    const displayedColumns = RemediationActionsNavigationHelper.getDisplayedColumns();

    return CommonThreatsNavigationHelper.buildNavigationItem([this.dom.$widgetTitle], filters, displayedColumns);
  }

  protected getNavigationItemsForWidgetBody(targets: Array<Target>, timeRange: number): Array<NavigationItem> {
    const legendItems = this.getLegendItems();

    const navigationItems: Array<NavigationItem> = [];

    legendItems.forEach((legendItem: HTMLElement, elementIndex: number) => {
      navigationItems.push(this.buildNavigationItemForLegendItem(legendItem, targets, timeRange, elementIndex));
    });

    return navigationItems;
  }

  private buildNavigationItemForLegendItem(
    legendItem: HTMLElement,
    targets: Array<Target>,
    timeRange: number,
    elementIndex: number
  ) {
    const itemKey = Object.keys(this.latestData)[elementIndex];
    const displayedColumns = RemediationActionsNavigationHelper.getDisplayedColumns();
    const isPartner = Application.Main.company.isPartner();
    const filters = RemediationActionsNavigationHelper.getFiltersForNavigationItem({
      itemKey: itemKey,
      timeRange: timeRange,
      targets: targets,
      isPartner: isPartner,
    });

    return CommonThreatsNavigationHelper.buildNavigationItem([legendItem], filters, displayedColumns);
  }

  private updateChartAnimationSetting(displayMode: DisplayMode) {
    this.basicChartOptions.chart.animations.enabled = displayMode === DisplayMode.DASHBOARD;
  }

  private getLegendItems(): NodeList {
    return this.$chart.el.querySelectorAll('.apexcharts-legend-series');
  }

  protected updateDrillDownHandlers() {
    if (!this.dom.$widgetTitle || !this.getLegendItems().length) {
      return;
    }

    super.updateDrillDownHandlers();
  }
}
